import { PropTypes } from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useFormikContext } from 'formik';
import { makeStyles } from '@material-ui/core';
import EditableCard, {
  Edit,
  EditContent,
  ReadOnly,
  ReadOnlyContent
} from '~/modules/common/components/EditableCard';
import { TimeAndExpensesIcon } from '~/modules/common/components/Icons';
import { Expense } from '../Expenses';
import TimeAndExpenseCardContent from './TimeAndExpenseCardContent';

const cardIcon = <TimeAndExpensesIcon />;
const cardTitle = <FormattedMessage id="projectTimeAndExpenseCard.title" />;

const useStyles = makeStyles(theme => ({
  titleRoot: {
    display: 'flex'
  },
  expenseLabel: {
    marginLeft: theme.spacing(2)
  },
  icon: {
    marginTop: theme.spacing(0.3)
  },
  root: {
    paddingBottom: theme.spacing(1.6)
  }
}));

export const TimeAndExpense = ({
  projectPermissions = {},
  editable,
  isProjectLoading
}) => {
  const { handleReset, handleSubmit } = useFormikContext();

  return (
    <EditableCard
      editable={editable}
      dataQeId="Project_TimeAndExpense"
      ariaLabelKey="projectTimeAndExpenseCard.timeAndExpenseCardDialog"
    >
      <ReadOnly
        avatar={cardIcon}
        title={cardTitle}
        isLoading={isProjectLoading}
      >
        <ReadOnlyContent>
          <TimeAndExpenseCardContent
            editable={false}
            projectPermissions={projectPermissions}
          />
        </ReadOnlyContent>
      </ReadOnly>
      <Edit onSave={handleSubmit} onCancel={handleReset}>
        <EditContent>
          <TimeAndExpenseCardContent
            editable
            projectPermissions={projectPermissions}
          />
        </EditContent>
      </Edit>
    </EditableCard>
  );
};

TimeAndExpense.propTypes = {
  editable: PropTypes.bool,
  projectPermissions: PropTypes.object,
  isProjectLoading: PropTypes.bool
};

const TimeAndExpenseForm = ({
  projectUri,
  expenseCodes,
  projectPermissions,
  editable,
  projectCurrency,
  projectDetails: { expenseBudgetedCost, startDate, endDate },
  onSetBillPlanChanged,
  hasBillingContractAssignment,
  isBillingContractType
}) => {
  const classes = useStyles();

  const { canViewExpenseCodes } = projectPermissions;

  return (
    <div className={classes.root} data-qe-id="ExpensesCard">
      {canViewExpenseCodes && (
        <Expense
          editable={editable}
          endDate={endDate}
          expenseBudgetedCost={expenseBudgetedCost}
          expenseCodes={expenseCodes}
          hasBillingContractAssignment={hasBillingContractAssignment}
          isBillingContractType={isBillingContractType}
          onSetBillPlanChanged={onSetBillPlanChanged}
          projectCurrency={projectCurrency}
          projectId={projectUri}
          projectPermissions={projectPermissions}
          startDate={startDate}
        />
      )}
    </div>
  );
};

TimeAndExpenseForm.propTypes = {
  editable: PropTypes.bool,
  projectUri: PropTypes.string.isRequired,
  expenseCodes: PropTypes.array,
  projectPermissions: PropTypes.object,
  projectCurrency: PropTypes.object,
  projectDetails: PropTypes.object,
  hasBillingContractAssignment: PropTypes.bool,
  onSetBillPlanChanged: PropTypes.func,
  isBillingContractType: PropTypes.bool
};

export default TimeAndExpenseForm;
